/**
 * Dynamic tab handling
 *
 * @copyright 2024 Linguistica 360
 */

import $ from 'jquery';

import { Script, injector } from '../shared/injector.js';
import { $wrap } from '../shared/utils.js';
import {
    renderAIContent,
    resetNavMenu,
    showStoryPane,
} from '../ai-ui/InsightAI.js';

injector.injectClass(class extends Script {
    segments = [];
    segment = -1;
    activeRef = null;

    onLoad() {
        this.$tabNav = $('#dynamic_tab_nav');
        if (!this.$tabNav.length) {
            return;
        }

        this.$player = this.$tabNav.parent().find('.player');
        if (!this.$player.length) {
            console.error('No player for dynamic tabs');
        }

        this.setupTabListener();
        this.setupPlayerListener();
        this.setupTabButtons();
        this.openInitialTab();
    }

    setupTabListener() {
        this.$tabNav.on('click', 'a', this.$wrap(($a) => {
            this.onTabClick($a);
        }));
    }

    setupPlayerListener() {
        if (this.$player.data('cutoff')) {
            return;
        }

        this.$tabNav.parent().find('.audio-anchor').each($wrap(($this) => {
            const time = $this.data('time');
            const ref = $this.data('ref');
            const $a = this.$tabNav.find(`a[data-ref='${ref}']`);
            this.segments.push({ time, ref, $a });
        }));

        this.segments.sort((a, b) => a.time - b.time);

        this.$player.on('event-player-time', (_, time) => {
            const segment = this.findSegmentIndex(time);

            if (segment !== this.segment) {
                this.onTabClick(this.segments[segment].$a, false, true);
                this.segment = segment;
            }
        });
    }

    setupTabButtons() {
        const $tabs = this.$tabNav.parent().find('.dynamic-tab');
        const length = $tabs.length;

        $tabs.each($wrap(($tab, i) => {
            const $div = $('<div class="dynamic-tabs-buttons">');

            let $prev = $('<span>');
            if (i > 0) {
                $prev = $('<a class="button secondary">Previous</a>');
                const $prevTab = $tabs.eq(i - 1);
                const $prevTabButton = this.$tabNav.find(`a[data-ref='${$prevTab.data('tab')}']`);
                $prev.on('click', () => {
                    this.onTabClick($prevTabButton);
                    this.scrollIntoView();
                });
            }

            let $next = $('<span>');
            if (i < length - 1) {
                $next = $('<a class="button secondary">Next</a>');
                const $nextTab = $tabs.eq(i + 1);
                const $nextTabButton = this.$tabNav.find(`a[data-ref='${$nextTab.data('tab')}']`);
                $next.on('click', () => {
                    this.onTabClick($nextTabButton);
                    this.scrollIntoView();
                });
            }

            $div.append($prev, $next);
            $tab.append($div);
        }));
    }

    openInitialTab() {
        const initialRef = (location.hash || '#intro').substring(1);
        let $initialTab = this.$tabNav.find(`a[data-ref='${initialRef}']`);
        if (!$initialTab.length) {
            $initialTab = this.$tabNav.find('a').first();
        }

        this.onTabClick($initialTab, true);
    }

    onTabClick($a, init = false, noAutoSeek = false) {
        const ref = $a.data('ref');
        if (ref === this.activeRef) { //avoid calling twice on same tab
            return;
        }
        this.activeRef = ref;

        this.$tabNav.find('a').parent().removeClass('active');
        $a.parent().addClass('active');

        const $container = this.$tabNav.parent();
        $container.find('.dynamic-tab:not(.hidden)').hide();

        resetNavMenu();

        const $tabContent = $container.find(`.dynamic-tab[data-tab="${ref}"]`);
        $tabContent.show();

        showStoryPane();

        history.replaceState(undefined, undefined, `#${ref}`);

        const $anchor = $tabContent.find('.audio-anchor');
        if (init) {
            const time = $anchor.data('time');

            if (time !== undefined) {
                this.$player.data('init-time', time);
                this.segment = this.findSegmentIndex(time);
            }
        } else if (!noAutoSeek) {
            let pause = ref.endsWith('-lesson') || ref.startsWith('quiz-');
            if (pause) {
                this.$player.find('.player-control.player-pause').trigger('click');
            } else {
                $anchor.trigger('my-tab-clicked', [pause]);
            }
        }

        //Show Buttons or Load Conversation
        renderAIContent(ref).catch((error) => {
            console.error('Unable to render capsule', error) ;
        });

    }

    findSegmentIndex(time) {
        for (let i = 0; i < this.segments.length; i++) {
            if (time < this.segments[i].time) {
                return i === 0 ? 0 : i - 1;
            }
        }

        return this.segments.length - 1;
    }

    scrollIntoView() {
        this.$tabNav[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
});
